/**
 * KPIs, as AgencyAnalytics lays them out: one table across clients at agency
 * level (/kpis) and the same table pre-scoped inside a client. Create KPI
 * opens the modal; each row's menu offers Add To Dashboard, Edit, Duplicate,
 * Refresh and Delete.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { EmptyFeature } from '../agency/EmptyFeature.tsx';
import { customApi, type CustomMetric } from '../custom/custom-api.ts';
import { withCustom } from '../custom/with-custom.ts';
import type { Catalogue, Client } from '../reports/reports-api.ts';
import { KpiModal } from './KpiModal.tsx';
import { KpiTable } from './KpiTable.tsx';
import { kpisApi, type Kpi } from './kpis-api.ts';

export function KpiPage({
  organisationId,
  clients,
  catalogue,
  clientId,
}: {
  organisationId: string;
  clients: Client[];
  catalogue: Catalogue | null;
  /** Inside a client: only its KPIs, and new ones belong to it. */
  clientId: string | null;
}): ReactNode {
  const [kpis, setKpis] = useState<Kpi[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [editing, setEditing] = useState<Kpi | 'new' | null>(null);
  const [custom, setCustom] = useState<CustomMetric[]>([]);
  useEffect(() => {
    customApi
      .metrics(organisationId, clientId)
      .then(setCustom)
      .catch(() => setCustom([]));
  }, [organisationId, clientId]);
  // Names and formats of custom metrics for the table (C6).
  const named = useMemo(
    () => (catalogue ? withCustom(catalogue, custom) : null),
    [catalogue, custom],
  );

  const load = useCallback(() => {
    kpisApi
      .list(organisationId, clientId)
      .then((rows) => {
        setKpis(rows);
        setProblem(null);
      })
      .catch((error: unknown) =>
        setProblem(error instanceof Error ? error.message : 'KPIs could not be loaded.'),
      );
  }, [organisationId, clientId]);
  useEffect(load, [load]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (kpis ?? []).filter(
      (kpi) =>
        (!filterClient || kpi.business_unit_id === filterClient) &&
        (!term ||
          kpi.title.toLowerCase().includes(term) ||
          kpi.client_name.toLowerCase().includes(term)),
    );
  }, [kpis, search, filterClient]);

  const replace = (kpi: Kpi) =>
    setKpis((current) => {
      const list = current ?? [];
      return list.some((item) => item.id === kpi.id)
        ? list.map((item) => (item.id === kpi.id ? kpi : item))
        : [kpi, ...list];
    });

  const create = () => setEditing('new');
  const isEmpty = kpis !== null && kpis.length === 0;
  return (
    <section className="ag-page kp" aria-labelledby="kp-title">
      <header className="ag-head">
        <h1 id="kp-title">KPIs</h1>
        <span className="ag-spacer" />
        {!isEmpty && (
          <>
            {!clientId && (
              <label className="kp-filter">
                <span className="sr-only">Client</span>
                <select
                  value={filterClient}
                  onChange={(event) => setFilterClient(event.target.value)}
                >
                  <option value="">All clients</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="ag-search">
              <span className="sr-only">Search KPIs</span>
              <input
                type="search"
                placeholder="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
          </>
        )}
        <button
          type="button"
          className="btn"
          onClick={create}
          disabled={!catalogue || clients.length === 0}
        >
          Create KPI
        </button>
      </header>
      <div className="ag-body">
        {problem && (
          <p className="rb-banner" role="alert">
            {problem}
          </p>
        )}
        {kpis === null && !problem && <p className="ag-loading">Loading…</p>}
        {isEmpty && (
          <EmptyFeature
            title="Track the numbers that matter"
            lead="Pick a metric, decide what good looks like, and watch progress against it."
            points={[
              'Set one-time or recurring targets, with a suggestion from last month.',
              'See at a glance which clients are on track and which are behind.',
              'Get an alert in your inbox when a number crosses a line.',
            ]}
            action={{ label: 'Create KPI', onClick: create }}
          />
        )}
        {kpis !== null && kpis.length > 0 && named && (
          <KpiTable
            organisationId={organisationId}
            catalogue={named}
            kpis={rows}
            total={kpis.length}
            showClient={!clientId}
            onEdit={setEditing}
            onChanged={replace}
            onRemoved={(id) => setKpis((current) => (current ?? []).filter((kpi) => kpi.id !== id))}
            onProblem={setProblem}
          />
        )}
      </div>
      {editing && catalogue && (
        <KpiModal
          organisationId={organisationId}
          catalogue={catalogue}
          clients={clients}
          clientId={clientId}
          kpi={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(kpi) => {
            replace(kpi);
            setEditing(kpi);
          }}
        />
      )}
    </section>
  );
}
